import React, { RefObject, useRef, useState } from "react";

import useUpdateScadaConfig from "hooks/useUpdateScadaConfig";
import Draggable, { DraggableData } from "react-draggable";
import sanitizeHtml from "sanitize-html";
import useDashboardStore from "store/dashboard-store";
import useScadaStore from "store/scada-store";
import { LabelLayer, ParentRect, ScadaFreeText } from "../scadaTypes";
import { calculateLabelTransform } from "../utils/scadaUtils";

type ScadaFreeTextsProps = {
  freeTexts: ScadaFreeText[];
  scadaId: string | undefined;
  parent: ParentRect;
  gridSize: number;
  labelLayers: LabelLayer[];
};

const ScadaFreeTexts: React.FC<ScadaFreeTextsProps> = ({
  freeTexts,
  scadaId,
  parent,
  gridSize,
  labelLayers,
}) => {
  return (
    <>
      {[...(freeTexts ?? [])].map(ft => {
        const layer = labelLayers.find(g => g.layer === ft.layer);
        if (layer && !layer.visible) return null;
        return (
          <ScadaFreeTextLabel
            key={ft.id}
            freeText={ft}
            freeTexts={freeTexts}
            gridSize={gridSize}
            parent={parent}
            scadaId={scadaId}
          />
        );
      })}
    </>
  );
};

export default ScadaFreeTexts;

type ScadaFreeTextLabelProps = {
  freeText: ScadaFreeText;
} & Omit<ScadaFreeTextsProps, "labelLayers">;

const ScadaFreeTextLabel: React.FC<ScadaFreeTextLabelProps> = ({
  freeText,
  freeTexts,
  parent,
  gridSize,
  scadaId,
}) => {
  const [position] = useState(() => ({ x: freeText.x, y: freeText.y }));
  const updateFreeTexts = useScadaStore(state => state.updateFreeTexts);
  const updateScadaComponent = useUpdateScadaConfig();
  const isEditMode = useDashboardStore(state => state.isEditMode);

  const ref = useRef<HTMLElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const { height, width } = divRef.current
    ? divRef.current.getBoundingClientRect()
    : { height: 0, width: 0 };

  const cleanHtml = sanitizeHtml(freeText.text);

  const getLabelXY = (dragData: DraggableData) => {
    const { left: labelX, top: labelY } = ref.current!.getBoundingClientRect();

    if (labelX && labelY && parent.width && parent.height) {
      const relX = labelX - parent.left + dragData.deltaX;
      const relY = labelY - parent.top + dragData.deltaY;
      let pX = Math.round((relX / parent.width) * 1000) / 10;
      let pY = Math.round((relY / parent.height) * 1000) / 10;
      pX = pX <= 0 ? 0 : pX;
      pY = pY <= 0 ? 0 : pY;
      return { x: pX, y: pY };
    }

    return { x: 0, y: 0 };
  };

  const onStop = (data: DraggableData) => {
    if (!parent || !scadaId) return;

    const { x, y } = getLabelXY(data);
    const updated = freeTexts.map(ft => (ft.id === freeText?.id ? { ...ft, x, y } : ft));
    updateFreeTexts(scadaId, updated);
    updateScadaComponent({ freeTexts: updated });
  };

  const grid: [number, number] = [
    (parent.width / 100) * gridSize,
    (parent.height / 100) * gridSize,
  ];

  return (
    <Draggable
      nodeRef={ref as RefObject<HTMLElement>}
      bounds="parent"
      scale={1}
      grid={grid}
      disabled={!isEditMode}
      onStop={(_, dragData) => onStop(dragData)}
    >
      <span
        ref={ref}
        className="absolute"
        style={{
          top: `${position.y}%`,
          left: `${position.x}%`,
          cursor: isEditMode ? "move" : "default",
          width,
          height,
        }}
      >
        <div
          id={`${freeText.id}-${scadaId}`}
          ref={divRef}
          className="free-text-label"
          style={{
            color: freeText.color,
            fontSize: `${freeText.fontSize}px`,
            transform: `scale(${calculateLabelTransform(parent.width)})`,
            transformOrigin: "top left",
            width: "max-content",
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </span>
    </Draggable>
  );
};
